import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const SessionManagementSection = ({ onTerminateSession, onTerminateAllSessions }) => {
  const [activeSessions, setActiveSessions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    // Mock active sessions data
    const mockSessions = [
      {
        id: 'sess_001',
        userId: 1,
        userName: 'John Smith',
        email: 'john.smith@company.com',
        ipAddress: '192.168.1.100',
        location: 'New York, NY',
        device: 'Windows 11 - Chrome 118.0',
        loginTime: new Date(Date.now() - 2 * 60 * 60 * 1000),
        lastActivity: new Date(Date.now() - 15 * 60 * 1000),
        status: 'active'
      },
      {
        id: 'sess_002',
        userId: 2,
        userName: 'Sarah Johnson',
        email: 'sarah.johnson@company.com',
        ipAddress: '192.168.1.105',
        location: 'San Francisco, CA',
        device: 'macOS 14 - Safari 17.0',
        loginTime: new Date(Date.now() - 4 * 60 * 60 * 1000),
        lastActivity: new Date(Date.now() - 5 * 60 * 1000),
        status: 'active'
      },
      {
        id: 'sess_003',
        userId: 3,
        userName: 'Mike Chen',
        email: 'mike.chen@company.com',
        ipAddress: '192.168.1.110',
        location: 'Chicago, IL',
        device: 'Ubuntu 22.04 - Firefox 119.0',
        loginTime: new Date(Date.now() - 6 * 60 * 60 * 1000),
        lastActivity: new Date(Date.now() - 30 * 60 * 1000),
        status: 'idle'
      },
      {
        id: 'sess_004',
        userId: 4,
        userName: 'Emily Davis',
        email: 'emily.davis@company.com',
        ipAddress: '10.0.0.50',
        location: 'Remote - VPN',
        device: 'Windows 11 - Edge 118.0',
        loginTime: new Date(Date.now() - 8 * 60 * 60 * 1000),
        lastActivity: new Date(Date.now() - 2 * 60 * 1000),
        status: 'active'
      },
      {
        id: 'sess_005',
        userId: 5,
        userName: 'David Wilson',
        email: 'david.wilson@company.com',
        ipAddress: '192.168.1.120',
        location: 'Austin, TX',
        device: 'macOS 14 - Chrome 118.0',
        loginTime: new Date(Date.now() - 12 * 60 * 60 * 1000),
        lastActivity: new Date(Date.now() - 45 * 60 * 1000),
        status: 'idle'
      }
    ];

    setActiveSessions(mockSessions);
  }, []);

  const filteredSessions = activeSessions?.filter(session =>
    session?.userName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    session?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    session?.ipAddress?.includes(searchTerm) ||
    session?.location?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  const formatDuration = (date) => {
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const formatLastActivity = (date) => {
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / (1000 * 60));
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-success text-success-foreground';
      case 'idle':
        return 'bg-warning text-warning-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getDeviceIcon = (device) => {
    if (device?.includes('Windows')) return 'Monitor';
    if (device?.includes('macOS')) return 'Laptop';
    if (device?.includes('Ubuntu') || device?.includes('Linux')) return 'Terminal';
    if (device?.includes('iOS')) return 'Smartphone';
    if (device?.includes('Android')) return 'Tablet';
    return 'Monitor';
  };

  const handleTerminateSession = (sessionId) => {
    setActiveSessions(prev => prev?.filter(session => session?.id !== sessionId));
    onTerminateSession(sessionId);
  };

  const handleTerminateAllSessions = () => {
    setActiveSessions([]);
    onTerminateAllSessions();
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-card-foreground">Active Sessions</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Monitor and manage active user sessions across the system
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              iconName="RefreshCw"
              iconPosition="left"
            >
              Refresh
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleTerminateAllSessions}
              iconName="LogOut"
              iconPosition="left"
              disabled={activeSessions?.length === 0}
            >
              Terminate All
            </Button>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-success rounded-full"></div>
              <span className="text-sm text-muted-foreground">
                {activeSessions?.filter(s => s?.status === 'active')?.length} Active
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-warning rounded-full"></div>
              <span className="text-sm text-muted-foreground">
                {activeSessions?.filter(s => s?.status === 'idle')?.length} Idle
              </span>
            </div>
          </div>
          <div className="w-64">
            <Input
              type="search"
              placeholder="Search sessions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
            />
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="p-4 text-left font-medium text-card-foreground">User</th>
              <th className="p-4 text-left font-medium text-card-foreground">Device & Location</th>
              <th className="p-4 text-left font-medium text-card-foreground">Session Info</th>
              <th className="p-4 text-left font-medium text-card-foreground">Status</th>
              <th className="p-4 text-left font-medium text-card-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredSessions?.map((session) => (
              <tr key={session?.id} className="border-t border-border hover:bg-muted/30 transition-smooth">
                <td className="p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                      <span className="text-primary-foreground font-medium text-sm">
                        {session?.userName?.split(' ')?.map(n => n?.[0])?.join('')}
                      </span>
                    </div>
                    <div>
                      <div className="font-medium text-card-foreground">{session?.userName}</div>
                      <div className="text-sm text-muted-foreground">{session?.email}</div>
                    </div>
                  </div>
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-2 mb-1">
                    <Icon name={getDeviceIcon(session?.device)} size={16} className="text-muted-foreground" />
                    <span className="text-sm text-card-foreground">{session?.device}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="MapPin" size={14} className="text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">{session?.location}</span>
                  </div>
                  <div className="flex items-center space-x-2 mt-1">
                    <Icon name="Globe" size={14} className="text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">{session?.ipAddress}</span>
                  </div>
                </td>
                <td className="p-4">
                  <div className="text-sm text-card-foreground mb-1">
                    Duration: {formatDuration(session?.loginTime)}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Last activity: {formatLastActivity(session?.lastActivity)}
                  </div>
                </td>
                <td className="p-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(session?.status)}`}>
                    {session?.status?.charAt(0)?.toUpperCase() + session?.status?.slice(1)}
                  </span>
                </td>
                <td className="p-4">
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleTerminateSession(session?.id)}
                    iconName="LogOut"
                    iconPosition="left"
                  >
                    Terminate
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {filteredSessions?.length === 0 && (
        <div className="p-8 text-center">
          <Icon name="Monitor" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h4 className="text-lg font-medium text-card-foreground mb-2">No active sessions</h4>
          <p className="text-muted-foreground">
            {searchTerm ? 'No sessions match your search criteria.' : 'There are currently no active user sessions.'}
          </p>
        </div>
      )}
    </div>
  );
};

export default SessionManagementSection;